<script setup lang="ts">
import { BasicTestimonial, TestimonialSettings, TestimonialContent } from './types'
import TestimonialCarousel from './TestimonialCarousel.vue'
import TestimonialBasic from '@components/TestimonialBasic.vue'
import Section from '../Section.vue'
import { getColorById } from '../utils'
import { computed } from 'vue'
import { useModalStore } from '@/stores/ModalStore'
import { TestimonialResult } from '@components/customer_page/AssetSelectorModal/types'

interface Props {
  content?: TestimonialContent[]
  settings?: TestimonialSettings
  editable?: boolean
  sectionId: string
}

const props = withDefaults(defineProps<Props>(), {
  content: () => [],
  editable: false,
  settings: () => ({
    design_settings: {
      colors: [],
      fonts: [],
    },
    layout: 'carousel',
  }),
})

const emit = defineEmits<{
  (e: 'contentSelected', sectionId: string, assets: TestimonialResult[]): void
}>()

const actualSettings = computed(() => ({
  layout: 'carousel',
  title: 'What our customers say about us',
  show_title: true,
  ...props.settings,
}))

const formatTestimonial = (testimonial: TestimonialContent): BasicTestimonial => {
  return {
    ...testimonial,
    avatar_url: testimonial.image?.blob_url ?? testimonial.avatar_url,
    attribution_line_1: testimonial.attribution_line_1 || testimonial.author_name,
    attribution_line_2: testimonial.attribution_line_2 || testimonial.author_title,
    attribution_line_3: testimonial.attribution_line_3 || testimonial.author_company,
  }
}

const masonryOptions = {
  breakAt: {
    768: { columns: 1 },
    992: { columns: 2 },
    1200: { columns: 3 },
  },
  columns: 3,
  margin: 20,
}

// NOTE: This is needed because content can be passed as null,
// which overrides the [] value set by `withDefaults`
// and breaks the whole thing. So instead of null checking everywhere,
// compute it as an empty array.
const localContent = computed(() => props.content || [])

const textColor = computed(() =>
  getColorById(actualSettings.value.design_settings?.colors, 'text_color'),
)
const backgroundColor = computed(() =>
  getColorById(actualSettings.value.design_settings?.colors, 'background_color'),
)
const modalStore = useModalStore()

const openAssetPickerModal = (): void => {
  modalStore.open('asset_selector_modal', {
    assets: props.content,
    maxNumberOfAssets: 3,
    onAssetsSelected: handleAssetsSelected,
    title: 'Select Featured Testimonials',
    type: 'RenderableTestimonial',
  })
}

const handleAssetsSelected = (assets: any[]): void => {
  emit('contentSelected', props.sectionId, assets)
}
</script>

<template>
  <Section :settings="actualSettings">
    <div class="testimonial-section">
      <UeTypography
        v-if="editable && localContent?.length === 0"
        :contrast-with-color="backgroundColor"
      >
        No testimonials added yet.
      </UeTypography>
      <TestimonialCarousel
        v-if="settings.layout === 'carousel'"
        :editable="editable"
        :testimonials="localContent.map(formatTestimonial)"
        :testimonialTextColor="textColor"
      />
      <UeMasonry v-else :options="masonryOptions">
        <div v-for="testimonial in localContent.slice(0, 3)" :key="testimonial.id" class="asset">
          <TestimonialBasic
            :account="{}"
            :renderable-testimonial="formatTestimonial(testimonial)"
            :showFooter="false"
            :showHeadshotOrAvatar="!!testimonial.enable_avatar"
            renderAsList
          />
        </div>
      </UeMasonry>
      <div v-if="editable" class="d-flex justify-content-center">
        <button class="btn fs-sm btn-secondary mt-3" @click="openAssetPickerModal">
          Browse and add testimonials
        </button>
      </div>
    </div>
  </Section>
</template>

<style scoped lang="sass">
.testimonial-section
  margin-top: 2rem
</style>
